import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useFlashMessage } from './FlashMessage';
import { LoadingSpinner } from './ui';
import LeaveRequestInlineCalendar from './LeaveRequestInlineCalendar';
import { AuthContext } from '../context/AuthContext';
import { uploadSickNoteComplete, listSickNoteDocuments } from '../services/docuwareService';
import leaveService from '../services/leaveService';
import './LeaveRequestModal.css';  // Reuse main modal styles
import './EditLeaveRequestModal.css';  // Additional edit-specific styles

function EditLeaveRequestModal({ isOpen, onClose, onRequestUpdated, leaveRequest, leaveBalance, existingLeaveRequests = [] }) {
  const { showSuccess, showError } = useFlashMessage();
  const { user } = useContext(AuthContext);
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data - pre-populated from existing request
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    start_day_type: 'full',
    end_day_type: 'full',
    reason: ''
  });
  
  // Calculated values
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [affectedYears, setAffectedYears] = useState([]);
  const [spansMultipleYears, setSpansMultipleYears] = useState(false);
  
  // Warning state
  const [willRevertStatus, setWillRevertStatus] = useState(false);
  const [workAssignmentExists, setWorkAssignmentExists] = useState(false);
  
  // Consecutive sick days state (includes adjacent requests)
  const [consecutiveSickDays, setConsecutiveSickDays] = useState(0);
  const [requiresSickNote, setRequiresSickNote] = useState(false);
  
  // Sick note state (reuse from LeaveRequestModal.js)
  const [sickNoteFile, setSickNoteFile] = useState(null);
  const [sickNoteUploading, setSickNoteUploading] = useState(false);
  const [sickNoteUploaded, setSickNoteUploaded] = useState(false);
  const [sickNoteInfo, setSickNoteInfo] = useState(null);
  const [sickNoteMode, setSickNoteMode] = useState('upload'); // 'upload' or 'select'
  const [availableSickNotes, setAvailableSickNotes] = useState([]);
  const [selectedExistingSickNote, setSelectedExistingSickNote] = useState(null);
  const [loadingSickNotes, setLoadingSickNotes] = useState(false);

  // Pre-populate form data when modal opens or leaveRequest changes
  useEffect(() => {
    if (isOpen && leaveRequest) {
      setFormData({
        start_date: leaveRequest.start_date || '',
        end_date: leaveRequest.end_date || '',
        start_day_type: leaveRequest.start_day_type || 'full',
        end_day_type: leaveRequest.end_day_type || 'full',
        reason: leaveRequest.reason || ''
      });
      
      // Check if request will revert status
      setWillRevertStatus(leaveRequest.status === 'approved');
      
      // Check if work assignment exists
      checkWorkAssignment();
    }
  }, [isOpen, leaveRequest]);

  // Check if work assignment exists
  const checkWorkAssignment = async () => {
    if (!leaveRequest) return;
    
    try {
      const response = await axiosInstance.get(`/api/leave/requests/${leaveRequest.id}/work-assignment/`);
      setWorkAssignmentExists(!!response.data);
    } catch (error) {
      // 404 means no work assignment exists
      setWorkAssignmentExists(false);
    }
  };

  // Calculate blocked dates (exclude the current request being edited)
  const blockedDates = useMemo(() => {
    const blocked = new Set();
    
    // Filter for pending and approved leave only, exclude current request
    const activeLeave = existingLeaveRequests.filter(
      request => (request.status === 'pending' || request.status === 'approved') && 
                 request.id !== leaveRequest?.id
    );
    
    // For each active leave request, add all dates in the range to the blocked set
    activeLeave.forEach(request => {
      if (!request.start_date || !request.end_date) return;
      
      const [startYear, startMonth, startDay] = request.start_date.split('-').map(Number);
      const [endYear, endMonth, endDay] = request.end_date.split('-').map(Number);
      
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      const current = new Date(start);
      
      while (current <= end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        blocked.add(dateStr);
        current.setDate(current.getDate() + 1);
      }
    });
    
    return blocked;
  }, [existingLeaveRequests, leaveRequest]);

  // Fetch affected years when dates change
  const fetchAffectedYears = useCallback(async () => {
    if (!formData.start_date || !formData.end_date) {
      setCalculatedDays(0);
      setAffectedYears([]);
      setSpansMultipleYears(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_day_type: formData.start_day_type,
        end_day_type: formData.end_day_type
      });

      const response = await axiosInstance.get(`/api/leave/affected-years/?${params.toString()}`);
      const data = response.data;

      setCalculatedDays(data.total_days);
      setAffectedYears(data.affected_years || []);
      setSpansMultipleYears(data.spans_multiple_years);
    } catch (error) {
      console.error('Error calculating affected years:', error);
      setCalculatedDays(0);
      setAffectedYears([]);
      setSpansMultipleYears(false);
    }
  }, [formData.start_date, formData.end_date, formData.start_day_type, formData.end_day_type]);

  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      fetchAffectedYears();
    }
  }, [formData.start_date, formData.end_date, formData.start_day_type, formData.end_day_type, fetchAffectedYears]);

  // Calculate consecutive sick days when dates change and leave type is Sick Leave
  useEffect(() => {
    const calculateConsecutiveDays = async () => {
      if (!leaveRequest) return;
      
      const leaveType = leaveRequest.leave_type_details;
      
      if (leaveType?.name === 'Sick Leave' && formData.start_date && formData.end_date) {
        try {
          const result = await leaveService.calculateConsecutiveSickDays(
            formData.start_date,
            formData.end_date,
            leaveRequest.id  // Exclude current request being edited
          );
          setConsecutiveSickDays(result.consecutive_days);
          setRequiresSickNote(result.requires_sick_note);
          
          console.log(`Consecutive sick days (edit): ${result.consecutive_days}, Requires sick note: ${result.requires_sick_note}`);
        } catch (error) {
          console.error('Error calculating consecutive sick days:', error);
          // Fallback to using just the current request days
          setConsecutiveSickDays(calculatedDays);
          setRequiresSickNote(calculatedDays >= 3);
        }
      } else {
        // Reset if not sick leave
        setConsecutiveSickDays(0);
        setRequiresSickNote(false);
      }
    };
    
    calculateConsecutiveDays();
  }, [leaveRequest, formData.start_date, formData.end_date, calculatedDays]);

  // Fetch available sick notes when sick note is required
  useEffect(() => {
    const fetchSickNotes = async () => {
      if (requiresSickNote && user?.id) {
        setLoadingSickNotes(true);
        try {
          const response = await listSickNoteDocuments(user.id);
          if (response && response.Items) {
            setAvailableSickNotes(response.Items);
          }
        } catch (error) {
          console.error('Error fetching sick notes:', error);
          setAvailableSickNotes([]);
        } finally {
          setLoadingSickNotes(false);
        }
      } else {
        setAvailableSickNotes([]);
      }
    };
    
    fetchSickNotes();
  }, [requiresSickNote, user]);

  const handleDateChange = (start, end) => {
    if (start && end) {
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const formattedStart = formatDate(start);
      const formattedEnd = formatDate(end);
      
      setFormData(prev => ({
        ...prev,
        start_date: formattedStart,
        end_date: formattedEnd
      }));
    }
  };

  const handleDayTypeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReasonChange = (e) => {
    setFormData(prev => ({
      ...prev,
      reason: e.target.value
    }));
  };

  // Sick note file handling
  const handleSickNoteFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showError('Invalid file type. Please upload a PDF, JPG, or PNG file.');
      return;
    }
    
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      showError('File size exceeds 10MB. Please upload a smaller file.');
      return;
    }
    
    setSickNoteFile(file);
    setSickNoteInfo({
      name: file.name,
      size: file.size,
      uploadDate: new Date().toISOString()
    });
  };

  const removeSickNoteFile = () => {
    setSickNoteFile(null);
    setSickNoteInfo(null);
    // Reset file input
    const fileInput = document.getElementById('edit-sick-note-file-input');
    if (fileInput) fileInput.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    if (!leaveRequest) return;

    // Validation
    if (!formData.start_date || !formData.end_date) {
      showError('Please select start and end dates');
      return;
    }

    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      showError('Start date cannot be after end date');
      return;
    }

    // Step 1: Validate sick note requirement
    if (requiresSickNote && !sickNoteFile && !selectedExistingSickNote && !leaveRequest.sick_note_id) {
      showError(
        consecutiveSickDays > calculatedDays 
          ? `Sick note required: ${consecutiveSickDays} consecutive days (with prior leave)`
          : 'Sick note required for 3 or more consecutive days'
      );
      return;
    }

    try {
      setSubmitting(true);

      // Step 2: Handle sick note upload/link if required and not already attached
      let sickNoteData = {};
      if (requiresSickNote && !leaveRequest.sick_note_id) {
        if (sickNoteFile) {
          // Upload new sick note
          try {
            setSickNoteUploading(true);
            
            // Use leave request ID as sick note ID (must be integer for DocuWare)
            const sickNoteId = leaveRequest.id;
            
            // Upload to DocuWare (creates record + uploads file)
            const uploadResult = await uploadSickNoteComplete(
              user.id,
              sickNoteId,
              sickNoteFile,
              formData.reason
            );
            
            sickNoteData = {
              sick_note_id: sickNoteId.toString(),
              sick_note_docuware_document_id: uploadResult.documentId,
              sick_note_name: sickNoteFile.name,
              sick_note_description: formData.reason,
              sick_note_uploaded_at: new Date().toISOString()
            };
            
            setSickNoteUploaded(true);
            console.log('Sick note uploaded successfully during edit');
          } catch (uploadError) {
            console.error('Error uploading sick note:', uploadError);
            showError('Failed to upload sick note. Please try again.');
            return;
          } finally {
            setSickNoteUploading(false);
          }
        } else if (selectedExistingSickNote) {
          // Link existing sick note
          try {
            setSickNoteUploading(true);
            
            // Get the sick note details from the available sick notes
            const selectedNote = availableSickNotes.find(note => note.Id == selectedExistingSickNote);
            
            // Try multiple sources to get the actual filename
            let fileName = selectedNote?.Fields?.find(f => f.FieldName === 'SICK_NOTE_NAME')?.Item;
            if (!fileName) {
              fileName = selectedNote?.Fields?.find(f => f.FieldName === 'DWSTOREDFILENAME')?.Item;
            }
            if (!fileName) {
              fileName = selectedNote?.Fields?.find(f => f.FieldName === 'FILENAME')?.Item;
            }
            if (!fileName) {
              fileName = selectedNote?.ContentFileName;
            }
            if (!fileName) {
              fileName = `sick_note_${selectedExistingSickNote}.pdf`;
            }
            
            sickNoteData = {
              sick_note_id: leaveRequest.id.toString(),
              sick_note_docuware_document_id: selectedExistingSickNote,
              sick_note_name: fileName,
              sick_note_description: formData.reason,
              sick_note_uploaded_at: new Date().toISOString()
            };
            
            setSickNoteUploaded(true);
            console.log('Existing sick note linked successfully during edit');
          } catch (linkError) {
            console.error('Error linking existing sick note:', linkError);
            showError('Failed to link sick note. Please try again.');
            return;
          } finally {
            setSickNoteUploading(false);
          }
        }
      }

      // Step 3: Submit edit with sick note data
      const editData = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_day_type: formData.start_day_type,
        end_day_type: formData.end_day_type,
        reason: formData.reason.trim(),
        ...sickNoteData  // Include sick note fields if required
      };

      const response = await leaveService.editLeaveRequest(leaveRequest.id, editData);

      showSuccess('Leave request updated successfully!');
      
      if (onRequestUpdated) {
        onRequestUpdated();
      }
      
      handleClose();
    } catch (error) {
      console.error('Error updating leave request:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail || 
                          'Failed to update leave request';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      start_date: '',
      end_date: '',
      start_day_type: 'full',
      end_day_type: 'full',
      reason: ''
    });
    setCalculatedDays(0);
    setAffectedYears([]);
    setSpansMultipleYears(false);
    setWillRevertStatus(false);
    setWorkAssignmentExists(false);
    
    // Clear sick note state
    setSickNoteFile(null);
    setSickNoteInfo(null);
    setSickNoteUploading(false);
    setSickNoteUploaded(false);
    setSickNoteMode('upload');
    setAvailableSickNotes([]);
    setSelectedExistingSickNote(null);
    setLoadingSickNotes(false);
    setConsecutiveSickDays(0);
    setRequiresSickNote(false);
    
    // Reset file input
    const fileInput = document.getElementById('edit-sick-note-file-input');
    if (fileInput) fileInput.value = '';
    
    onClose();
  };

  if (!isOpen) return null;
  if (!leaveRequest) return null;

  const leaveType = leaveRequest.leave_type_details || {};

  return (
    <div className="leave-request-modal-overlay-unique" onClick={handleClose}>
      <div 
        className="leave-request-modal-unique" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="leave-request-modal-header">
          <h2 className="leave-request-modal-title">Edit Leave Request</h2>
          <button 
            className="leave-request-modal-close" 
            onClick={handleClose}
            aria-label="Close modal"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="leave-request-modal-body">
          {loading ? (
            <div className="leave-request-modal-loading">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="leave-request-form-step">
              {/* Warning Banners */}
              {willRevertStatus && (
                <div className="edit-leave-warning-banner">
                  <div className="edit-leave-warning-banner-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="edit-leave-warning-banner-content">
                    <div className="edit-leave-warning-banner-title">Status Will Be Reverted</div>
                    <p className="edit-leave-warning-banner-text">
                      This leave request is currently <strong>approved</strong>. After editing, it will revert to <strong>pending</strong> status and require re-approval from your supervisor.
                    </p>
                  </div>
                </div>
              )}

              {workAssignmentExists && (
                <div className="edit-leave-warning-banner">
                  <div className="edit-leave-warning-banner-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="edit-leave-warning-banner-content">
                    <div className="edit-leave-warning-banner-title">Work Assignment Will Be Removed</div>
                    <p className="edit-leave-warning-banner-text">
                      Work coverage has already been assigned. Editing this request will invalidate the existing work assignment, and a new assignment will need to be made upon re-approval.
                    </p>
                  </div>
                </div>
              )}

              {/* Read-only Leave Type */}
              <div className="edit-leave-readonly-field">
                <label className="edit-leave-readonly-label">Leave Type</label>
                <div className="edit-leave-readonly-value">
                  <span 
                    className="edit-leave-type-dot" 
                    style={{backgroundColor: leaveType.color || '#4CAF50'}}
                  ></span>
                  {leaveType.name || 'Unknown'}
                </div>
                <div className="edit-leave-readonly-note">
                  Leave type cannot be changed. To change the type, please cancel this request and create a new one.
                </div>
              </div>

              {/* Date Selection - Using same structure as LeaveRequestModal Step 2 */}
              <div className="leave-request-date-selection">
                <LeaveRequestInlineCalendar
                  startDate={formData.start_date ? new Date(formData.start_date) : null}
                  endDate={formData.end_date ? new Date(formData.end_date) : null}
                  onDateSelect={handleDateChange}
                  minDate={new Date()}
                  blockedDates={blockedDates}
                />

                {/* Day Type Selection */}
                {leaveType.allows_partial_days && formData.start_date && formData.end_date && (
                  <div className="leave-request-day-types">
                    <div className="leave-request-input-group">
                      <label className="leave-request-label">Start Day Type</label>
                      <select
                        className="leave-request-select"
                        value={formData.start_day_type}
                        onChange={(e) => handleDayTypeChange('start_day_type', e.target.value)}
                      >
                        <option value="full">Full Day</option>
                        <option value="morning">Morning Half</option>
                        <option value="afternoon">Afternoon Half</option>
                      </select>
                    </div>
                    <div className="leave-request-input-group">
                      <label className="leave-request-label">End Day Type</label>
                      <select
                        className="leave-request-select"
                        value={formData.end_day_type}
                        onChange={(e) => handleDayTypeChange('end_day_type', e.target.value)}
                      >
                        <option value="full">Full Day</option>
                        <option value="morning">Morning Half</option>
                        <option value="afternoon">Afternoon Half</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Date and calculation display */}
                {(formData.start_date || formData.end_date) && (
                  <div className="leave-request-calculation-display">
                    {formData.start_date && (
                      <div className="leave-request-calculation-row">
                        <span className="leave-request-calc-label">Start Date:</span>
                        <span className="leave-request-calc-value">
                          {new Date(formData.start_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    {formData.end_date && (
                      <div className="leave-request-calculation-row">
                        <span className="leave-request-calc-label">End Date:</span>
                        <span className="leave-request-calc-value">
                          {new Date(formData.end_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    {formData.start_date && formData.end_date && calculatedDays > 0 && (
                      <>
                        <div className="leave-request-calculation-row">
                          <span className="leave-request-calc-label">Total Days:</span>
                          <span className="leave-request-calc-value">
                            {calculatedDays % 1 === 0 ? Math.floor(calculatedDays) : calculatedDays.toFixed(1)} days
                          </span>
                        </div>
                        
                        {/* Multi-year breakdown */}
                        {spansMultipleYears && affectedYears.length > 0 && (
                          <div className="leave-request-year-breakdown">
                            <div className="leave-request-year-breakdown-header">
                              <i className="fas fa-calendar-alt" style={{marginRight: '8px'}}></i>
                              Multi-Year Leave Breakdown
                            </div>
                            {affectedYears.map((yearData) => {
                              const yearBalance = yearData.balance;
                              const category = leaveType.balance_category;
                              const available = category === 'personal_bereavement'
                                ? yearBalance?.personal_bereavement_available
                                : yearBalance?.available_days;
                              
                              return (
                                <div key={yearData.year} className="leave-request-year-item">
                                  <div className="leave-request-year-label">
                                    Year {yearData.year}:
                                  </div>
                                  <div className="leave-request-year-details">
                                    <span className="leave-request-year-days">
                                      {yearData.days % 1 === 0 ? Math.floor(yearData.days) : yearData.days.toFixed(1)} days
                                    </span>
                                    {category !== 'unlimited' && (
                                      <span className="leave-request-year-balance">
                                        (Balance: {available || 0} days)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Single year balance display */}
                        {!spansMultipleYears && leaveType.balance_category !== 'unlimited' && (
                          <div className="leave-request-calculation-row">
                            <span className="leave-request-calc-label">Current Balance:</span>
                            <span className="leave-request-calc-value">
                              {leaveType.balance_category === 'personal_bereavement' 
                                ? (leaveBalance?.personal_bereavement_available || 0)
                                : (leaveBalance?.available_days || 0)
                              } days
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Reason - Using same structure as Step 3 */}
              <div className="leave-request-additional-info">
                <div className="leave-request-input-group">
                  <label className="leave-request-label">
                    Reason {leaveType.requires_reason && <span className="leave-request-required">*</span>}
                  </label>
                  <textarea
                    className="leave-request-textarea"
                    value={formData.reason}
                    onChange={handleReasonChange}
                    placeholder="Provide reason for your leave request..."
                    rows={4}
                    required={leaveType.requires_reason}
                  />
                </div>

                {/* Sick Note Upload - Required for consecutive sick leave >= 3 days */}
                {requiresSickNote && (
                  <div className="sick-note-upload-section">
                    <div className="sick-note-required-alert">
                      <i className="fas fa-info-circle" style={{marginRight: '8px'}}></i>
                      {consecutiveSickDays > calculatedDays ? (
                        <span>Sick note required: <strong>{consecutiveSickDays} consecutive days</strong> (with prior leave)</span>
                      ) : (
                        'Sick note required for 3 or more consecutive days'
                      )}
                    </div>
                    
                    {/* Display existing sick note if already attached */}
                    {leaveRequest.sick_note_id && leaveRequest.sick_note_name && (
                      <div className="sick-note-existing-alert">
                        <div className="sick-note-existing-header">
                          <i className="fas fa-check-circle" style={{color: '#28a745', marginRight: '8px'}}></i>
                          <span>Sick note already attached</span>
                        </div>
                        <div className="sick-note-existing-details">
                          <span className="sick-note-existing-filename">{leaveRequest.sick_note_name}</span>
                          {leaveRequest.sick_note_uploaded_at && (
                            <span className="sick-note-existing-date">
                              Uploaded: {new Date(leaveRequest.sick_note_uploaded_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Only show upload/select UI if no sick note is already attached */}
                    {!leaveRequest.sick_note_id && (
                      <>
                        {/* Radio buttons for upload mode selection */}
                        <div className="sick-note-mode-selector">
                          <label className="sick-note-radio-option">
                            <input
                              type="radio"
                              name="sickNoteMode"
                              value="upload"
                              checked={sickNoteMode === 'upload'}
                              onChange={(e) => {
                                setSickNoteMode(e.target.value);
                                setSelectedExistingSickNote(null);
                              }}
                            />
                            <span>Upload New Sick Note</span>
                          </label>
                          <label className="sick-note-radio-option">
                            <input
                              type="radio"
                              name="sickNoteMode"
                              value="select"
                              checked={sickNoteMode === 'select'}
                              onChange={(e) => {
                                setSickNoteMode(e.target.value);
                                setSickNoteFile(null);
                                setSickNoteInfo(null);
                              }}
                            />
                            <span>Select Existing Sick Note</span>
                          </label>
                        </div>

                        {/* Upload New Mode */}
                        {sickNoteMode === 'upload' && (
                          <>
                            {!sickNoteFile ? (
                              <div className="sick-note-file-input-wrapper">
                                <input
                                  type="file"
                                  id="edit-sick-note-file-input"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={handleSickNoteFileChange}
                                  style={{display: 'none'}}
                                />
                                <button
                                  type="button"
                                  className="sick-note-upload-btn"
                                  onClick={() => document.getElementById('edit-sick-note-file-input').click()}
                                >
                                  <i className="fas fa-file-medical" style={{marginRight: '8px'}}></i>
                                  Upload Sick Note
                                </button>
                                <span className="sick-note-file-hint">PDF, JPG, or PNG (max 10MB)</span>
                              </div>
                            ) : (
                              <div className="sick-note-info-card">
                                <div className="sick-note-info-card-header">
                                  <i className="fas fa-check-circle sick-note-info-card-icon"></i>
                                  <span className="sick-note-info-card-title">Sick Note Attached</span>
                                  <button
                                    type="button"
                                    className="sick-note-close-x"
                                    onClick={removeSickNoteFile}
                                    aria-label="Remove sick note"
                                  >
                                    ×
                                  </button>
                                </div>
                                <div className="sick-note-info-card-body">
                                  <div className="sick-note-info-row">
                                    <span className="sick-note-info-label">File:</span>
                                    <span className="sick-note-info-value">{sickNoteInfo?.name}</span>
                                  </div>
                                  <div className="sick-note-info-row">
                                    <span className="sick-note-info-label">Size:</span>
                                    <span className="sick-note-info-value">{formatFileSize(sickNoteInfo?.size || 0)}</span>
                                  </div>
                                  <div className="sick-note-info-row">
                                    <span className="sick-note-info-label">Date:</span>
                                    <span className="sick-note-info-value">
                                      {new Date(sickNoteInfo?.uploadDate).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Select Existing Mode */}
                        {sickNoteMode === 'select' && (
                          <div className="sick-note-select-wrapper">
                            {loadingSickNotes ? (
                              <div className="sick-note-loading">
                                <LoadingSpinner />
                                <span>Loading available sick notes...</span>
                              </div>
                            ) : availableSickNotes.length > 0 ? (
                              <div className="sick-note-list">
                                <label className="sick-note-list-label">Select a sick note:</label>
                                <select
                                  className="sick-note-select-dropdown"
                                  value={selectedExistingSickNote || ''}
                                  onChange={(e) => setSelectedExistingSickNote(e.target.value)}
                                >
                                  <option value="">-- Choose a sick note --</option>
                                  {availableSickNotes.map((note) => {
                                    const docId = note.Id;
                                    const fileName = note.Fields?.find(f => f.FieldName === 'SICK_NOTE_NAME')?.Item || 'Sick Note';
                                    const dateUploaded = note.Fields?.find(f => f.FieldName === 'DATE_UPLOADED')?.Item;
                                    const displayDate = dateUploaded ? new Date(dateUploaded).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short', 
                                      day: 'numeric' 
                                    }) : 'Unknown date';
                                    
                                    return (
                                      <option key={docId} value={docId}>
                                        {fileName} - Uploaded: {displayDate}
                                      </option>
                                    );
                                  })}
                                </select>
                                {selectedExistingSickNote && (
                                  <div className="sick-note-selected-info">
                                    <i className="fas fa-check-circle" style={{color: '#28a745', marginRight: '8px'}}></i>
                                    <span>Sick note selected</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="sick-note-empty-state">
                                <i className="fas fa-folder-open" style={{fontSize: '32px', color: '#999', marginBottom: '10px'}}></i>
                                <p>No existing sick notes found.</p>
                                <p style={{fontSize: '14px', color: '#666'}}>Switch to "Upload New Sick Note" to upload one.</p>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="leave-request-modal-footer">
          <button 
            className="leave-request-button leave-request-button-secondary" 
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            className="edit-leave-submit-button" 
            onClick={handleSubmit}
            disabled={submitting || !formData.start_date || !formData.end_date}
          >
            {submitting ? 'Loading...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditLeaveRequestModal;

